import { useCallback, useContext } from 'react';

import { TokenContext } from '@/Contexts/TokenContext';
import {
  AdminApplicationExportEmailRequestDto,
  ApplicationStatusEmailRequestDto,
  NewReviewerInvitationEmailRequestDto,
  NewUserJoinedPartnerGroupEmailRequestDto,
  PartnerExportEmailRequestDto,
  TreeLineReceiptEmailRequestDto,
} from '@/models/Admin/Emails/EmailRequestDtos';
import { EmailRecordProps, EmailTypeProps, SendEmailResponseProps } from '@/models/Email';
import { baseUrl } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';

export interface UseEmailRecordReturn {
  fetchEmailActivity: () => Promise<EmailRecordProps[]>;
  getEmailTypes: () => Promise<EmailTypeProps[]>;
  sendAdminApplicationExportEmail: (
    request: AdminApplicationExportEmailRequestDto
  ) => Promise<SendEmailResponseProps | null>;
  sendNewUserJoinedPartnerGroupEmail: (
    request: NewUserJoinedPartnerGroupEmailRequestDto
  ) => Promise<SendEmailResponseProps | null>;
  sendNewReviewerInvitationEmail: (
    request: NewReviewerInvitationEmailRequestDto
  ) => Promise<SendEmailResponseProps | null>;
  sendTreeLineReceiptEmail: (
    request: TreeLineReceiptEmailRequestDto
  ) => Promise<SendEmailResponseProps | null>;
  sendPartnerExportEmail: (
    request: PartnerExportEmailRequestDto
  ) => Promise<SendEmailResponseProps | null>;
  sendApplicationStatusEmail: (
    request: ApplicationStatusEmailRequestDto
  ) => Promise<SendEmailResponseProps | null>;
}

export default function useEmailRecord(): UseEmailRecordReturn {
  const { authResult } = useContext(TokenContext);
  const { customFetch } = useCustomFetch();
  const baseEmailUrl = `${baseUrl}emailRecord`;
  const fetchEmailActivity = useCallback(async (): Promise<EmailRecordProps[]> => {
    const fetchUrl = `${baseEmailUrl}`;

    const response = await customFetch<EmailRecordProps[]>(fetchUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    return response;
  }, [authResult.accessToken, baseEmailUrl, customFetch]);

  const getEmailTypes = useCallback(async (): Promise<EmailTypeProps[]> => {
    const fetchUrl = `${baseEmailUrl}/emailtypes`;

    const response = await customFetch<EmailTypeProps[]>(fetchUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    return response;
  }, [authResult.accessToken, baseEmailUrl, customFetch]);

  const postEmail = useCallback(
    async <TRequest>(
      endpoint: string,
      request: TRequest
    ): Promise<SendEmailResponseProps | null> => {
      const fetchUrl = `${baseEmailUrl}/${endpoint}`;

      const response = await customFetch<SendEmailResponseProps | null>(fetchUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(request),
      });

      return response;
    },
    [authResult.accessToken, baseEmailUrl, customFetch]
  );

  const sendAdminApplicationExportEmail = useCallback(
    async (
      request: AdminApplicationExportEmailRequestDto
    ): Promise<SendEmailResponseProps | null> => {
      return await postEmail('sendemail/admin-application-export', request);
    },
    [postEmail]
  );

  const sendNewUserJoinedPartnerGroupEmail = useCallback(
    async (
      request: NewUserJoinedPartnerGroupEmailRequestDto
    ): Promise<SendEmailResponseProps | null> => {
      return await postEmail('sendemail/new-user-joined-partner-group', request);
    },
    [postEmail]
  );

  const sendNewReviewerInvitationEmail = useCallback(
    async (
      request: NewReviewerInvitationEmailRequestDto
    ): Promise<SendEmailResponseProps | null> => {
      return await postEmail('sendemail/new-reviewer-invitation', request);
    },
    [postEmail]
  );

  const sendTreeLineReceiptEmail = useCallback(
    async (request: TreeLineReceiptEmailRequestDto): Promise<SendEmailResponseProps | null> => {
      return await postEmail('sendemail/tree-line-receipt', request);
    },
    [postEmail]
  );

  const sendPartnerExportEmail = useCallback(
    async (request: PartnerExportEmailRequestDto): Promise<SendEmailResponseProps | null> => {
      return await postEmail('sendemail/partner-export', request);
    },
    [postEmail]
  );

  const sendApplicationStatusEmail = useCallback(
    async (request: ApplicationStatusEmailRequestDto): Promise<SendEmailResponseProps | null> => {
      return await postEmail('sendemail/application-status', request);
    },
    [postEmail]
  );

  return {
    fetchEmailActivity,
    getEmailTypes,
    sendAdminApplicationExportEmail,
    sendNewUserJoinedPartnerGroupEmail,
    sendNewReviewerInvitationEmail,
    sendTreeLineReceiptEmail,
    sendPartnerExportEmail,
    sendApplicationStatusEmail,
  };
}
